<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseToggle from '@/components/base/BaseToggle.vue'
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { getMacro, createMacro, updateMacro } from '@/api/api.js'
import { useToast } from '@/composables/useToast.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import MdiPlus from '~icons/mdi/plus'
import MdiDelete from '~icons/mdi/delete'
import MdiDragVertical from '~icons/mdi/drag-vertical'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronUp from '~icons/mdi/chevron-up'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const toast = useToast()

const isNew = computed(() => route.params.macroId === 'new')
const loading = ref(false)
const saving = ref(false)
const loadError = ref(null)
const isDirty = ref(false)

// --- form state ---
const name = ref('')
const description = ref('')
const isActive = ref(true)
const modelType = ref('')  // '' | 'project' | 'bundle' | 'item_metadata'
const inputs = ref([]) // [{name, label, type, default}]
const steps = ref([])  // [{_key, order, label, action_type, params}]

const MODEL_TYPES = computed(() => [
  { value: '', label: t('macros.modelGeneral') },
  { value: 'project', label: t('macros.modelProject') },
  { value: 'bundle', label: t('macros.modelBundle') },
  { value: 'item_metadata', label: t('macros.modelItemInstance') },
])

const ACTION_TYPES = computed(() => [
  { value: 'create_project', label: t('macros.actionCreateProject') },
  { value: 'add_bundle_to_project', label: t('macros.actionAddBundle') },
  { value: 'checkout_bundle_to_user', label: t('macros.actionCheckoutBundleUser') },
  { value: 'checkout_bundle_to_project', label: t('macros.actionCheckoutBundleProject') },
  { value: 'create_item_checkout', label: t('macros.actionCreateCheckout') },
  { value: 'update_project_status', label: t('macros.actionUpdateProjectStatus') },
])

const ACTION_PARAM_SCHEMAS = {
  create_project: {
    required: ['name', 'start_time', 'end_time'],
    optional: ['location', 'description', 'status'],
    fields: [
      { key: 'name', required: true, type: 'text', notes: t('projectView.projectNamePlaceholder') },
      { key: 'start_time', required: true, type: 'date', notes: t('ui.isoDatetimeEG20260601t080000z') },
      { key: 'end_time', required: true, type: 'date', notes: t('ui.isoDatetime') },
      { key: 'location', required: false, type: 'text', notes: t('ui.locationDescriptionString') },
      { key: 'description', required: false, type: 'text', notes: '' },
      { key: 'status', required: false, type: 'integer', notes: t('ui.projectstatusIntDefault0Draft') },
    ],
    outputs: ['project_id', 'project_name'],
  },
  add_bundle_to_project: {
    required: ['project_id', 'bundle_id'],
    optional: [],
    fields: [
      { key: 'project_id', required: true, type: 'integer', notes: t('ui.macroVariableExample', { example: '{{steps.N.project_id}}' }) },
      { key: 'bundle_id', required: true, type: 'integer', notes: '' },
    ],
    outputs: ['added_project_item_ids'],
  },
  checkout_bundle_to_user: {
    required: ['bundle_id', 'user_id'],
    optional: ['expected_checkin'],
    fields: [
      { key: 'bundle_id', required: true, type: 'integer', notes: '' },
      { key: 'user_id', required: true, type: 'integer', notes: '' },
      { key: 'expected_checkin', required: false, type: 'date', notes: t('ui.isoDatetime') },
    ],
    outputs: ['bundle_checkout_id', 'item_checkout_ids'],
  },
  checkout_bundle_to_project: {
    required: ['bundle_id', 'project_id'],
    optional: ['user_id', 'expected_checkin'],
    fields: [
      { key: 'bundle_id', required: true, type: 'integer', notes: '' },
      { key: 'project_id', required: true, type: 'integer', notes: t('ui.macroVariableExample', { example: '{{steps.N.project_id}}' }) },
      { key: 'user_id', required: false, type: 'integer', notes: t('ui.alsoAttachesTheUserToEachItemCheckout') },
      { key: 'expected_checkin', required: false, type: 'date', notes: t('ui.isoDatetime') },
    ],
    outputs: ['bundle_checkout_id', 'item_checkout_ids'],
  },
  create_item_checkout: {
    required: ['item_metadata_id'],
    optional: ['user_id', 'project_id', 'expected_checkin', 'quantity', 'notes'],
    fields: [
      { key: 'item_metadata_id', required: true, type: 'integer', notes: '' },
      { key: 'user_id', required: false, type: 'integer', notes: '' },
      { key: 'project_id', required: false, type: 'integer', notes: t('ui.macroVariableExample', { example: '{{steps.N.project_id}}' }) },
      { key: 'expected_checkin', required: false, type: 'date', notes: t('ui.isoDatetime') },
      { key: 'quantity', required: false, type: 'integer', notes: t('ui.defaultQuantity') },
      { key: 'notes', required: false, type: 'text', notes: '' },
    ],
    outputs: ['item_checkout_id'],
  },
  update_project_status: {
    required: ['project_id', 'status'],
    optional: [],
    fields: [
      { key: 'project_id', required: true, type: 'integer', notes: t('ui.macroVariableExample', { example: '{{steps.N.project_id}}' }) },
      { key: 'status', required: true, type: 'integer', notes: t('ui.0Draft1Confirmed2Ready3PickedUp4Delivered5Active6Returning7Completed8Cancelled') },
    ],
    outputs: ['project_id', 'old_status', 'new_status'],
  },
}

const INPUT_TYPES = computed(() => [
  { value: 'text', label: t('macros.inputTypeText') },
  { value: 'integer', label: t('macros.inputTypeInteger') },
  { value: 'date', label: t('macros.inputTypeDate') },
  { value: 'project', label: t('macros.inputTypeProject') },
  { value: 'user', label: t('macros.inputTypeUser') },
  { value: 'bundle', label: t('macros.inputTypeBundle') },
  { value: 'item_metadata', label: t('macros.inputTypeItemInstance') },
  { value: 'location', label: t('macros.inputTypeLocation') },
  { value: 'warehouse', label: t('macros.inputTypeWarehouse') },
])

let keyCounter = 0
function nextKey() { return ++keyCounter }

// expanded step index
const expandedStep = ref(null)

// drag-and-drop
const dragSrcIndex = ref(null)

// variable picker
const activeParamKey = ref(null) // {stepKey, paramKey}
const pickerPos = ref({ top: 0, left: 0 })
const showPicker = ref(false)

// validation errors
const errors = ref({})

function actionTypeLabel(val) {
  return ACTION_TYPES.value.find((a) => a.value === val)?.label ?? val
}

function actionParamSchema(actionType) {
  return ACTION_PARAM_SCHEMAS[actionType] ?? { required: [], optional: [] }
}

function actionOutputVars(actionType) {
  return actionParamSchema(actionType).outputs ?? []
}

function stepParamKeys(step) {
  return Object.keys(step.params ?? {}).map((k) => (k || '').trim()).filter(Boolean)
}

function missingRequiredParams(step) {
  const { required } = actionParamSchema(step.action_type)
  const present = new Set(stepParamKeys(step))
  return required.filter((k) => !present.has(k))
}

function addParamKey(step, key) {
  if (!key) return
  if (!step.params) step.params = {}
  if (!(key in step.params)) {
    step.params[key] = ''
    isDirty.value = true
  }
}

function addAllMissingRequired(step) {
  for (const key of missingRequiredParams(step)) {
    addParamKey(step, key)
  }
}

function nextSuggestedParamKey(step) {
  const { required, optional } = actionParamSchema(step.action_type)
  const present = new Set(stepParamKeys(step))
  const next = [...required, ...optional].find((k) => !present.has(k))
  return next ?? 'param_key'
}

// available variables for a given step index
function availableVars(stepIdx) {
  const inputVars = inputs.value.map((inp) => ({
    syntax: `{{${inp.name}}}`,
    label: inp.label || inp.name,
    group: t('macros.inputsSection'),
  }))
  const stepVars = []
  for (let i = 0; i < stepIdx; i++) {
    const s = steps.value[i]
    const outputs = actionOutputVars(s.action_type)
    if (outputs.length) {
      for (const out of outputs) {
        stepVars.push({
          syntax: `{{steps.${i}.${out}}}`,
          label: t('ui.stepOutput', { step: i + 1, output: out }),
          group: t('macros.stepOutputsGroup'),
        })
      }
    } else {
      stepVars.push({
        syntax: `{{steps.${i}.result}}`,
        label: t('ui.stepOutput', { step: i + 1, output: s.label || actionTypeLabel(s.action_type) || t('macros.stepOutputsGroup') }),
        group: t('macros.stepOutputsGroup'),
      })
    }
  }
  return { inputVars, stepVars }
}

function openPicker(stepKey, paramKey, event) {
  activeParamKey.value = { stepKey, paramKey }
  const rect = event.target.getBoundingClientRect()
  pickerPos.value = { top: rect.bottom + window.scrollY + 6, left: rect.left + window.scrollX }
  showPicker.value = true
}

function insertVar(syntax) {
  if (!activeParamKey.value) return
  const { stepKey, paramKey } = activeParamKey.value
  const step = steps.value.find((s) => s._key === stepKey)
  if (!step) return
  if (!step.params) step.params = {}
  const current = step.params[paramKey] ?? ''
  step.params[paramKey] = current + syntax
  showPicker.value = false
  isDirty.value = true
}

function closePicker() {
  showPicker.value = false
}

// --- inputs ---
function addInput() {
  inputs.value.push({ name: '', label: '', type: 'text', default: '' })
  isDirty.value = true
}

function removeInput(idx) {
  inputs.value.splice(idx, 1)
  isDirty.value = true
}

function deriveInputName(input) {
  input.name = input.label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  isDirty.value = true
}

// --- steps ---
function addStep() {
  steps.value.push({
    _key: nextKey(),
    order: steps.value.length,
    label: '',
    action_type: '',
    params: {},
  })
  expandedStep.value = steps.value[steps.value.length - 1]._key
  isDirty.value = true
}

function removeStep(key) {
  steps.value = steps.value.filter((s) => s._key !== key)
  renumberSteps()
  if (expandedStep.value === key) expandedStep.value = null
  isDirty.value = true
}

function renumberSteps() {
  steps.value.forEach((s, i) => { s.order = i })
}

function toggleStep(key) {
  expandedStep.value = expandedStep.value === key ? null : key
}

// param row management
function addParam(step) {
  if (!step.params) step.params = {}
  const blankExists = Object.keys(step.params).includes('')
  if (!blankExists) {
    step.params[''] = ''
  }
  isDirty.value = true
}

function updateParamKey(step, oldKey, newKey) {
  const val = step.params[oldKey]
  delete step.params[oldKey]
  step.params[newKey] = val
  isDirty.value = true
}

function removeParam(step, key) {
  delete step.params[key]
  isDirty.value = true
}

// --- drag and drop ---
function onDragStart(idx) {
  dragSrcIndex.value = idx
}

function onDragOver(idx) {
  if (dragSrcIndex.value === null || dragSrcIndex.value === idx) return
  const moved = steps.value.splice(dragSrcIndex.value, 1)[0]
  steps.value.splice(idx, 0, moved)
  dragSrcIndex.value = idx
  renumberSteps()
  isDirty.value = true
}

function onDragEnd() {
  dragSrcIndex.value = null
}

// --- load ---
async function load() {
  if (isNew.value) return
  loading.value = true
  loadError.value = null
  try {
    const m = await getMacro(route.params.macroId)
    hydrate(m)
  } catch (e) {
    loadError.value = e?.data?.detail ?? t('macros.loadError')
  } finally {
    loading.value = false
  }
}

function hydrate(m) {
  name.value = m.name ?? ''
  description.value = m.description ?? ''
  isActive.value = m.is_active ?? true
  modelType.value = m.model_type ?? ''
  const rawInputs = Array.isArray(m.inputs) ? m.inputs : Object.values(m.inputs ?? {})
  inputs.value = rawInputs.map((inp) => ({ ...inp }))
  steps.value = (m.steps ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((s) => ({
      _key: nextKey(),
      order: s.order,
      label: s.label ?? '',
      action_type: s.action_type ?? '',
      params: { ...(s.params ?? {}) },
    }))
  isDirty.value = false
}

// --- validate ---
function validate() {
  const errs = {}
  if (!name.value.trim()) errs.name = t('macros.nameRequired')
  inputs.value.forEach((inp, i) => {
    if (!inp.name.trim()) errs[`input_${i}_name`] = t('macros.nameRequired')
    if (!inp.type) errs[`input_${i}_type`] = t('macros.typeRequired')
  })
  steps.value.forEach((s, i) => {
    if (!s.action_type) errs[`step_${i}_action`] = t('macros.actionRequired')
  })
  errors.value = errs
  return Object.keys(errs).length === 0
}

// --- save ---
async function save() {
  if (!validate()) return
  saving.value = true
  try {
    const payload = {
      name: name.value.trim(),
      description: description.value.trim(),
      is_active: isActive.value,
      model_type: modelType.value,
      inputs: inputs.value.map(({ name: n, label, type, default: def }) => ({
        name: n, label, type, ...(def !== '' && def !== undefined ? { default: def } : {}),
      })),
      steps: steps.value.map((s) => ({
        order: s.order,
        label: s.label,
        action_type: s.action_type,
        params: s.params ?? {},
      })),
    }
    if (isNew.value) {
      const created = await createMacro(payload)
      isDirty.value = false
      toast.success(t('macros.createdToast'))
      router.replace(`/admin/macros/${created.id}`)
    } else {
      const updated = await updateMacro(route.params.macroId, payload)
      hydrate(updated)
      toast.success(t('macros.savedToast'))
    }
  } catch (e) {
    toast.error(e?.data?.detail ?? t('macros.saveError'))
  } finally {
    saving.value = false
  }
}

// unsaved changes guard
onBeforeRouteLeave((to, from, next) => {
  if (!isDirty.value) return next()
  if (window.confirm(t('macros.unsavedChanges'))) {
    next()
  } else {
    next(false)
  }
})

onMounted(load)

// close picker on outside click
function onDocClick(e) {
  if (showPicker.value && !e.target.closest('.var-picker') && !e.target.closest('[data-picker-trigger]')) {
    closePicker()
  }
}
onMounted(() => document.addEventListener('click', onDocClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocClick))
</script>

<template>
  <div class="page-view">
    <BasePageHeader :title="isNew ? $t('macros.new') : (name || $t('macros.edit'))" @back="router.push('/admin/macros')">
      <template #actions>
        <button class="btn primary small" :disabled="saving" @click="save">
          <svg v-if="saving" class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        {{ saving ? $t('macros.saving') : $t('macros.save') }}
        </button>
      </template>
    </BasePageHeader>

    <div v-if="loading" class="flex justify-center py-16">
      <svg class="w-8 h-8 text-[var(--color-primary)] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>

    <div v-else-if="loadError" class="p-5">
      <p class="text-red-400">{{ loadError }}</p>
    </div>

    <div v-else class="flex flex-col lg:flex-row gap-4 p-4 min-h-0">

      <!-- ── Left panel: metadata + inputs ───────────────────────── -->
      <div class="flex flex-col gap-4 lg:w-[420px] flex-shrink-0">

        <!-- Metadata card -->
        <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex flex-col gap-4">
          <h3 class="section-label m-0">{{ $t('macros.macroDetails') }}</h3>

          <BaseFormField :label="$t('common.name')" required :error="errors.name">
            <BaseInput
              v-model="name"
              :placeholder="$t('macros.namePlaceholder')"
              @input="isDirty = true"
            />
          </BaseFormField>

          <BaseFormField :label="$t('macros.descriptionLabel')">
            <BaseTextarea
              v-model="description"
              :rows="2"
              :placeholder="$t('macros.descriptionPlaceholder')"
              @input="isDirty = true"
            />
          </BaseFormField>

          <BaseFormField :label="$t('macros.activeLabel')">
            <BaseToggle v-model="isActive" :label="$t('macros.activeLabel')" @update:model-value="isDirty = true" />
          </BaseFormField>

          <BaseFormField :label="$t('macros.pageContextLabel')" :hint="$t('macros.pageContextHint')">
            <BaseSelect
              v-model="modelType"
              @update:model-value="isDirty = true"
            >
              <option v-for="mt in MODEL_TYPES" :key="mt.value" :value="mt.value">{{ mt.label }}</option>
            </BaseSelect>
          </BaseFormField>
        </div>

        <!-- Inputs editor -->
        <div class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-4 flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h3 class="section-label m-0">{{ $t('macros.inputsSection') }}</h3>
            <button class="btn ghost small" @click="addInput">
              <MdiPlus class="w-3.5 h-3.5" />
              {{ $t('macros.addInput') }}
            </button>
          </div>

          <div v-if="!inputs.length" class="text-sm text-[var(--color-muted)] text-center py-2">
            {{ $t('macros.noInputs') }}
          </div>

          <div
            v-for="(inp, idx) in inputs"
            :key="idx"
            class="flex flex-col gap-2 p-3 bg-[var(--color-surface-2)] rounded-lg border border-[var(--color-border)]"
          >
            <div class="flex gap-2">
              <BaseFormField class="flex-1" :label="$t('macros.labelInputLabel')" density="compact">
                <BaseInput
                  v-model="inp.label"
                  class="text-sm"
                  :placeholder="$t('macros.customerNamePlaceholder')"
                  @input="deriveInputName(inp)"
                />
              </BaseFormField>
              <BaseFormField class="flex-1" :label="$t('macros.typeInputLabel')" required density="compact">
                <BaseSelect v-model="inp.type" class="text-sm" @update:model-value="isDirty = true">
                  <option v-for="inputType in INPUT_TYPES" :key="inputType.value" :value="inputType.value">{{ inputType.label }}</option>
                </BaseSelect>
              </BaseFormField>
              <button class="btn-icon danger self-end mb-0.5" @click="removeInput(idx)">
                <MdiDelete class="w-4 h-4" />
              </button>
            </div>
            <div class="flex gap-2">
              <BaseFormField class="flex-1" :label="$t('macros.variableNameLabel')" density="compact" :error="errors[`input_${idx}_name`]">
                <BaseInput
                  v-model="inp.name"
                  class="text-sm font-mono"
                  :placeholder="$t('macros.variableNamePlaceholder')"
                  @input="isDirty = true"
                />
              </BaseFormField>
              <BaseFormField class="flex-1" :label="$t('macros.defaultLabel')" density="compact">
                <BaseInput
                  v-model="inp.default"
                  class="text-sm"
                  placeholder="—"
                  @input="isDirty = true"
                />
              </BaseFormField>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Right panel: steps ──────────────────────────────────── -->
      <div class="flex-1 min-w-0 flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h3 class="section-label m-0">{{ $t('macros.stepsSection') }}</h3>
          <button class="btn ghost small" @click="addStep">
            <MdiPlus class="w-3.5 h-3.5" />
            {{ $t('macros.addStep') }}
          </button>
        </div>

        <div v-if="!steps.length" class="text-sm text-[var(--color-muted)] text-center py-6">
          {{ $t('macros.noSteps') }}
        </div>

        <div
          v-for="(step, idx) in steps"
          :key="step._key"
          draggable="true"
          class="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl overflow-hidden transition-opacity"
          :class="dragSrcIndex === idx ? 'opacity-40' : ''"
          @dragstart="onDragStart(idx)"
          @dragover.prevent="onDragOver(idx)"
          @dragend="onDragEnd"
        >
          <!-- Step header (always visible) -->
          <div
            class="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none hover:bg-white/5"
            @click="toggleStep(step._key)"
          >
            <MdiDragVertical class="w-4 h-4 text-[var(--color-muted)] flex-shrink-0 cursor-grab" />
            <span class="w-6 h-6 rounded-full bg-[var(--color-surface-2)] border border-[var(--color-border)] flex items-center justify-center text-xs font-bold text-[var(--color-muted)] flex-shrink-0">
              {{ idx + 1 }}
            </span>
            <div class="flex-1 min-w-0 flex items-center gap-2">
              <span class="text-sm font-medium text-[var(--color-text)] truncate">
                {{ step.label || actionTypeLabel(step.action_type) || $t('macros.unnamedStep') }}
              </span>
              <span v-if="step.action_type" class="badge badge-accent text-xs flex-shrink-0">
                {{ actionTypeLabel(step.action_type) }}
              </span>
              <span v-if="errors[`step_${idx}_action`]" class="text-xs text-red-400 flex-shrink-0">{{ errors[`step_${idx}_action`] }}</span>
            </div>
            <button class="btn-icon danger flex-shrink-0" @click.stop="removeStep(step._key)">
              <MdiDelete class="w-4 h-4" />
            </button>
            <component :is="expandedStep === step._key ? MdiChevronUp : MdiChevronDown" class="w-4 h-4 text-[var(--color-muted)] flex-shrink-0" />
          </div>

          <!-- Step body (expanded) -->
          <div v-if="expandedStep === step._key" class="border-t border-[var(--color-border)] p-4 flex flex-col gap-4">

            <!-- Label -->
            <BaseFormField :label="$t('macros.stepLabelLabel')">
              <BaseInput
                v-model="step.label"
                :placeholder="$t('macros.stepDescPlaceholder')"
                @input="isDirty = true"
              />
            </BaseFormField>

            <!-- Action type -->
            <BaseFormField :label="$t('macros.actionTypeLabel')" required :error="errors[`step_${idx}_action`]">
              <BaseSelect v-model="step.action_type" @update:model-value="isDirty = true">
                <option value="" disabled>{{ $t('macros.selectAction') }}</option>
                <option v-for="a in ACTION_TYPES" :key="a.value" :value="a.value">{{ a.label }}</option>
              </BaseSelect>
            </BaseFormField>

            <!-- Params editor -->
            <div class="flex flex-col gap-2">
              <div class="flex items-center justify-between">
                <label class="form-label m-0">{{ $t('macros.parametersLabel') }}</label>
                <button class="btn ghost small" @click="addParam(step)">
                  <MdiPlus class="w-3 h-3" />
                  {{ $t('macros.addParam') }}
                </button>
              </div>

              <div v-if="step.action_type" class="bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded-lg p-2.5 flex flex-col gap-2">
                <div class="text-xs text-[var(--color-muted)]">
                  {{ $t('macros.requiredFor', { action: actionTypeLabel(step.action_type) }) }}
                  <span v-if="actionParamSchema(step.action_type).required.length" class="font-mono text-[var(--color-text)]">
                    {{ actionParamSchema(step.action_type).required.join(', ') }}
                  </span>
                  <span v-else class="text-[var(--color-muted)]">{{ $t('macros.requiredNone') }}</span>
                </div>

                <div v-if="missingRequiredParams(step).length" class="flex flex-wrap items-center gap-2">
                  <span class="text-xs text-red-400">{{ $t('macros.missingRequired', { params: missingRequiredParams(step).join(', ') }) }}</span>
                  <button class="btn ghost small" @click="addAllMissingRequired(step)">{{ $t('macros.addMissing') }}</button>
                </div>

                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="keyName in actionParamSchema(step.action_type).required"
                    :key="`req-${step._key}-${keyName}`"
                    class="px-2 py-0.5 rounded-md text-xs font-mono border transition-colors"
                    :class="stepParamKeys(step).includes(keyName)
                      ? 'border-emerald-600/40 bg-emerald-950/30 text-emerald-300'
                      : 'border-red-700/40 bg-red-950/30 text-red-300 hover:bg-red-900/40'"
                    @click="addParamKey(step, keyName)"
                  >{{ keyName }}</button>
                </div>
              </div>

              <div v-if="!Object.keys(step.params ?? {}).length" class="text-xs text-[var(--color-muted)]">
                {{ $t('macros.noParams') }}
              </div>

              <div
                v-for="(val, key) in step.params"
                :key="key"
                class="grid grid-cols-1 md:grid-cols-[minmax(180px,1fr)_minmax(260px,2fr)_auto] gap-2 items-start bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded-lg p-2"
              >
                <div class="flex flex-col gap-1">
                  <label class="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">{{ $t('macros.paramKey') }}</label>
                  <BaseInput
                    :model-value="key"
                    class="text-sm font-mono"
                    :placeholder="nextSuggestedParamKey(step)"
                    @change="updateParamKey(step, key, $event.target.value)"
                  />
                </div>
                <div class="flex-1 flex flex-col gap-1">
                  <label class="text-[11px] uppercase tracking-wide text-[var(--color-muted)]">{{ $t('macros.paramValue') }}</label>
                  <div class="flex gap-1 items-center">
                    <BaseInput
                      :model-value="val"
                      class="text-sm font-mono flex-1"
                      :placeholder="$t('macros.paramPlaceholder')"
                      @update:model-value="step.params[key] = $event; isDirty = true"
                    />
                    <button
                      class="btn ghost small flex-shrink-0 font-mono"
                      :title="$t('macros.insertVariable')"
                      data-picker-trigger
                      @click="openPicker(step._key, key, $event)"
                    >{{}}</button>
                  </div>
                </div>
                <button class="btn-icon danger flex-shrink-0 mt-5" @click="removeParam(step, key)">
                  <MdiDelete class="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            <!-- Variable picker (inline, context-aware) -->
            <div v-if="showPicker && activeParamKey?.stepKey === step._key" class="var-picker bg-[var(--color-surface-2)] border border-[var(--color-border)] rounded-lg p-3 flex flex-col gap-3">
              <div v-if="availableVars(idx).inputVars.length">
                <div class="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide mb-1.5">{{ $t('macros.inputsGroup') }}</div>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="v in availableVars(idx).inputVars"
                    :key="v.syntax"
                    class="px-2 py-1 rounded-md text-xs font-mono bg-[var(--color-primary)]/20 text-[var(--color-accent)] border border-[var(--color-primary)]/30 hover:bg-[var(--color-primary)]/30 transition-colors"
                    @click="insertVar(v.syntax)"
                  >{{ v.syntax }}</button>
                </div>
              </div>
              <div v-if="availableVars(idx).stepVars.length">
                <div class="text-xs font-semibold text-[var(--color-muted)] uppercase tracking-wide mb-1.5">{{ $t('macros.stepOutputsGroup') }}</div>
                <div class="flex flex-wrap gap-1.5">
                  <button
                    v-for="v in availableVars(idx).stepVars"
                    :key="v.syntax"
                    class="px-2 py-1 rounded-md text-xs font-mono bg-blue-900/30 text-blue-300 border border-blue-700/30 hover:bg-blue-900/50 transition-colors"
                    :title="v.label"
                    @click="insertVar(v.syntax)"
                  >{{ v.syntax }}</button>
                </div>
              </div>
              <div v-if="!availableVars(idx).inputVars.length && !availableVars(idx).stepVars.length" class="text-xs text-[var(--color-muted)]">
                {{ $t('macros.noVars') }}
              </div>
            </div>

          </div>
        </div>

        <button class="btn ghost" @click="addStep">
          <MdiPlus class="w-4 h-4" />
          {{ $t('macros.addStep') }}
        </button>
      </div>
    </div>
  </div>
</template>
